/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import type { uiTypes, CoreAppStore } from '@opengeoweb/store';
import { layerActions, mapSelectors, layerTypes } from '@opengeoweb/store';
import type { DimensionConfig } from '@opengeoweb/webmap-react';
import {
  dimensionConfig,
  DimensionSelectDialog,
  WEBMAP_REACT_NAMESPACE,
} from '@opengeoweb/webmap-react';
import DimensionSelectSliderConnect from './DimensionSelectSliderConnect';

interface DimensionSelectDialogConnectProps {
  mapId: string;
  dimensionName: string;
  isOpen: boolean;
  order?: number;
  onMouseDown?: () => void;
  onCloseDialog?: () => void;
  index?: number;
  source?: uiTypes.Source;
}

const DimensionSelectDialogConnect: React.FC<
  DimensionSelectDialogConnectProps
> = ({
  mapId,
  dimensionName,
  isOpen,
  order = 0,
  onMouseDown,
  onCloseDialog,
  index = 0,
  source = 'app',
}: DimensionSelectDialogConnectProps) => {
  const dispatch = useDispatch();
  // dimensions come from webmap-react
  const { t } = useTranslation(WEBMAP_REACT_NAMESPACE);

  const mapContainsDimension = useSelector((store: CoreAppStore) =>
    mapSelectors.getIsEnabledLayersForMapDimension(store, mapId, dimensionName),
  );

  const layerIds = useSelector((store: CoreAppStore) =>
    mapSelectors.getLayerIds(store, mapId),
  );

  const handleDimensionValueChanged = React.useCallback(
    (layerId: string, dimensionName: string, newValue: string): void => {
      dispatch(
        layerActions.layerChangeDimension({
          origin: layerTypes.LayerActionOrigin.layerManager,
          layerId,
          dimension: {
            name: dimensionName,
            currentValue: newValue,
          },
          mapId,
        }),
      );
    },
    [dispatch, mapId],
  );

  const handleSyncChanged = React.useCallback(
    (
      layerId: string,
      dimensionName: string,
      dimensionValue: string,
      synced: boolean,
    ): void => {
      dispatch(
        layerActions.layerChangeDimension({
          origin: layerTypes.LayerActionOrigin.layerManager,
          layerId,
          dimension: {
            name: dimensionName,
            currentValue: dimensionValue,
            synced,
          },
        }),
      );
    },
    [dispatch],
  );

  if (!mapContainsDimension || !layerIds || layerIds.length < 1) {
    return null;
  }

  const dimConfig: DimensionConfig = dimensionConfig(t).find(
    (cnf) => cnf.name === dimensionName,
  )!;
  const title = dimConfig ? dimConfig.label : dimensionName;

  return (
    <DimensionSelectDialog
      data-testid={`multidimensionContent-${title}`}
      dimensionName={dimensionName}
      index={index}
      isOpen={isOpen}
      onClose={onCloseDialog}
      onMouseDown={onMouseDown}
      order={order}
      source={source}
    >
      {layerIds.map((layerId) => (
        <DimensionSelectSliderConnect
          dimensionName={dimensionName}
          handleDimensionValueChanged={handleDimensionValueChanged}
          handleSyncChanged={handleSyncChanged}
          key={layerId}
          layerId={layerId}
          mapId={mapId}
        />
      ))}
    </DimensionSelectDialog>
  );
};
export default DimensionSelectDialogConnect;
